import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Grid,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledFileUploadField from '../../../../components/controlledComponents/ControlledFileUploadField.component';
import { TDocument } from '../../../../../apis/documents/documents.interfaces';
import {
  ILessonPlan,
  ELessonPlanStatus,
} from '../../../../../apis/academics/academicsLessonPlans.interfaces';
import { EAcademicBoard } from '../../../../../apis/academics/academicsClassesAndCourses.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import {
  useAcademicsLessonPlansState,
  useAcademicsLessonPlansActions,
} from '../../contexts/academicsLessonPlans.context';
import { useAcademicsClassesAndCoursesState } from '../../contexts/academicsClassesAndCourses.context';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsLessonPlansKey } from '../../hooks/useAcademicsLessonPlansHook';
import { useStaffListHook } from '../../../staff/hooks/useStaffListHook';
import { zNumberFromInput, zOptionalNumberFromInput } from 'src/utils/zodFormHelpers';

type LessonPlanFormData = {
  title: string;
  description?: string;
  academicYear: string;
  term?: string;
  board: EAcademicBoard;
  classId: number;
  courseId: number;
  teacherId: number;
  curriculumFramework?: 'CBSE' | 'ICSE' | 'NCERT' | 'STATE_BOARD' | 'OTHER';
  syllabusReference?: string;
  textbookReference?: string;
  ncertChapter?: string;
  subject?: string;
  gradeLevel?: string;
  tags?: string;
  status: ELessonPlanStatus;
  totalDuration: number;
  version?: number;
  documents: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: string;
  }[];
  tempDocument?: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: string;
  } | null;
};

const getLessonPlanFormSchema = (translateText: (key: string) => string) =>
  z.object({
    title: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().optional(),
    academicYear: z.string().min(1, translateText('common.fieldRequired')),
    term: z.string().optional(),
    board: z.nativeEnum(EAcademicBoard, {
      required_error: translateText('common.fieldRequired'),
    }),
    classId: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    courseId: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    teacherId: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    curriculumFramework: z
      .enum(['CBSE', 'ICSE', 'NCERT', 'STATE_BOARD', 'OTHER'])
      .optional(),
    syllabusReference: z.string().optional(),
    textbookReference: z.string().optional(),
    ncertChapter: z.string().optional(),
    subject: z.string().optional(),
    gradeLevel: z.string().optional(),
    tags: z.string().optional(),
    status: z.nativeEnum(ELessonPlanStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    totalDuration: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    version: zOptionalNumberFromInput(),
    documents: z.array(z.any()).optional(),
    tempDocument: z.any().optional().nullable(),
  });

const getBoardOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: EAcademicBoard.CBSE,
      value: translateText('academics.lessonPlans.boardOptions.cbse') || 'CBSE',
    },
    {
      key: EAcademicBoard.ICSE,
      value: translateText('academics.lessonPlans.boardOptions.icse') || 'ICSE',
    },
    {
      key: EAcademicBoard.STATE,
      value:
        translateText('academics.lessonPlans.boardOptions.state') ||
        'State Board',
    },
    {
      key: EAcademicBoard.IB,
      value:
        translateText('academics.lessonPlans.boardOptions.ib') ||
        'International Baccalaureate',
    },
    {
      key: EAcademicBoard.IGCSE,
      value: translateText('academics.lessonPlans.boardOptions.igcse') || 'IGCSE',
    },
    {
      key: EAcademicBoard.NIOS,
      value: translateText('academics.lessonPlans.boardOptions.nios') || 'NIOS',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: ELessonPlanStatus.DRAFT,
      value:
        translateText('academics.lessonPlans.statusOptions.draft') || 'Draft',
    },
    {
      key: ELessonPlanStatus.IN_REVIEW,
      value:
        translateText('academics.lessonPlans.statusOptions.inReview') ||
        'In Review',
    },
    {
      key: ELessonPlanStatus.APPROVED,
      value:
        translateText('academics.lessonPlans.statusOptions.approved') ||
        'Approved',
    },
    {
      key: ELessonPlanStatus.ACTIVE,
      value:
        translateText('academics.lessonPlans.statusOptions.active') || 'Active',
    },
    {
      key: ELessonPlanStatus.ARCHIVED,
      value:
        translateText('academics.lessonPlans.statusOptions.archived') ||
        'Archived',
    },
  ];

const getCurriculumFrameworkOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'CBSE',
      value:
        translateText('academics.lessonPlans.curriculumFrameworkOptions.cbse') ||
        'CBSE',
    },
    {
      key: 'ICSE',
      value:
        translateText('academics.lessonPlans.curriculumFrameworkOptions.icse') ||
        'ICSE',
    },
    {
      key: 'NCERT',
      value:
        translateText('academics.lessonPlans.curriculumFrameworkOptions.ncert') ||
        'NCERT',
    },
    {
      key: 'STATE_BOARD',
      value:
        translateText(
          'academics.lessonPlans.curriculumFrameworkOptions.stateBoard'
        ) || 'State Board',
    },
    {
      key: 'OTHER',
      value:
        translateText('academics.lessonPlans.curriculumFrameworkOptions.other') ||
        'Other',
    },
  ];

const AddEditLessonPlanView: FC = () => {
  const { translateText } = useTranslation();
  const lessonPlanFormSchema = getLessonPlanFormSchema(translateText);

  const {
    mode,
    selectedLessonPlan,
    isLoading: contextIsLoading,
  } = useAcademicsLessonPlansState();
  const { closeMode } = useAcademicsLessonPlansActions();

  const { createLessonPlan, updateLessonPlan } = useAcademicsService();
  const queryClient = useQueryClient();

  const [isLessonPlanLoading, setIsLessonPlanLoading] = useState(false);
  const [lessonPlanErrorMessage, setLessonPlanErrorMessage] = useState<
    string | null
  >(null);

  const { academicsClassesAndCoursesAppState } =
    useAcademicsClassesAndCoursesState();
  const { data: staffListResponse } = useStaffListHook({
    page: 1,
    limit: 1000,
  });

  const isOpen = mode === 'add' || mode === 'edit';
  const isCreateMode = mode === 'add';

  const classOptions = useMemo<TKeyValue<string | number>[]>(() => {
    return academicsClassesAndCoursesAppState.classes.map((cls) => ({
      key: cls.id,
      value: cls.name,
    }));
  }, [academicsClassesAndCoursesAppState.classes]);

  const courseOptions = useMemo<TKeyValue<string | number>[]>(() => {
    return academicsClassesAndCoursesAppState.courses.map((course) => ({
      key: course.id,
      value: `${course.name} (${course.subjectCode})`,
    }));
  }, [academicsClassesAndCoursesAppState.courses]);

  const teacherOptions = useMemo<TKeyValue<string | number>[]>(() => {
    if (!staffListResponse?.data) return [];
    return staffListResponse.data
      .map((staff) => {
        const parsedId = parseInt(staff.id, 10);
        if (isNaN(parsedId)) {
          return null;
        }
        return {
          key: parsedId,
          value:
            `${staff.basicDetails.firstName} ${staff.basicDetails.middleName || ''} ${staff.basicDetails.lastName}`.trim(),
        } as TKeyValue<string | number>;
      })
      .filter((item) => item !== null) as TKeyValue<string | number>[];
  }, [staffListResponse]);

  const defaultValues: LessonPlanFormData = {
    title: '',
    description: '',
    academicYear: new Date().getFullYear().toString(),
    term: '',
    board: EAcademicBoard.CBSE,
    classId: 0,
    courseId: 0,
    teacherId: 0,
    curriculumFramework: undefined,
    syllabusReference: '',
    textbookReference: '',
    ncertChapter: '',
    subject: '',
    gradeLevel: '',
    tags: '',
    status: ELessonPlanStatus.DRAFT,
    totalDuration: 0,
    version: undefined,
    documents: [],
    tempDocument: null,
  };

  const methods = useForm<LessonPlanFormData>({
    resolver: zodResolver(lessonPlanFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const tempDocument = watch('tempDocument');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  useEffect(() => {
    if (tempDocument?.documentName) {
      const newDoc = {
        documentName: tempDocument.documentName || '',
        title: tempDocument.title || tempDocument.documentName || '',
        description: tempDocument.description || '',
        verificationDate: tempDocument.verificationDate || '',
        status: tempDocument.status || '',
      };
      append(newDoc);
      setValue('tempDocument', null, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempDocument?.documentName]);

  useEffect(() => {
    if (isOpen && selectedLessonPlan && mode === 'edit') {
      reset({
        title: selectedLessonPlan.title || '',
        description: selectedLessonPlan.description || '',
        academicYear: selectedLessonPlan.academicYear || '',
        term: selectedLessonPlan.term || '',
        board: selectedLessonPlan.board || EAcademicBoard.CBSE,
        classId: selectedLessonPlan.classId || 0,
        courseId: selectedLessonPlan.courseId || 0,
        teacherId: selectedLessonPlan.teacherId || 0,
        curriculumFramework: selectedLessonPlan.curriculumFramework,
        syllabusReference: selectedLessonPlan.syllabusReference || '',
        textbookReference: selectedLessonPlan.textbookReference || '',
        ncertChapter: selectedLessonPlan.ncertChapter || '',
        subject: selectedLessonPlan.subject || '',
        gradeLevel: selectedLessonPlan.gradeLevel || '',
        tags: selectedLessonPlan.tags?.join(', ') || '',
        status: selectedLessonPlan.status || ELessonPlanStatus.DRAFT,
        totalDuration: selectedLessonPlan.totalDuration || 0,
        version: selectedLessonPlan.version,
        documents:
          selectedLessonPlan.documents?.map((doc) => ({
            documentName: doc.documentName || '',
            title: doc.description || doc.documentName || '',
            description: doc.description || '',
            verificationDate: doc.verificationDate || '',
            status: doc.status || '',
          })) || [],
        tempDocument: null,
      });
    } else if (isOpen && mode === 'add') {
      reset(defaultValues);
    }
    // eslint-disable-next-line
  }, [isOpen, selectedLessonPlan, mode, reset]);

  const handleClose = () => {
    setLessonPlanErrorMessage(null);
    closeMode();
    reset(defaultValues);
  };

  const onSubmitForm = async (data: LessonPlanFormData) => {
    setIsLessonPlanLoading(true);
    setLessonPlanErrorMessage(null);

    try {
      const lessonPlanData: Partial<ILessonPlan> = {
        title: data.title,
        description: data.description,
        academicYear: data.academicYear,
        term: data.term,
        board: data.board,
        classId: data.classId,
        courseId: data.courseId,
        teacherId: data.teacherId,
        curriculumFramework: data.curriculumFramework,
        syllabusReference: data.syllabusReference,
        textbookReference: data.textbookReference,
        ncertChapter: data.ncertChapter,
        subject: data.subject,
        gradeLevel: data.gradeLevel,
        tags: data.tags ? data.tags.split(',').map((tag) => tag.trim()) : [],
        status: data.status,
        totalDuration: data.totalDuration,
        version: data.version,
        units: [],
        activities: [],
        examples: [],
        documents:
          data.documents?.map(
            (doc) =>
              ({
                documentName: doc.documentName,
                description: doc.description || doc.title,
                verificationDate: doc.verificationDate,
                status: doc.status,
              }) as TDocument
          ) || [],
      };

      if (isCreateMode) {
        await createLessonPlan({ lessonPlan: lessonPlanData as any });
      } else {
        await updateLessonPlan(selectedLessonPlan!.id, lessonPlanData as any);
      }

      queryClient.invalidateQueries({ queryKey: [getAcademicsLessonPlansKey] });
      handleClose();
    } catch (error: any) {
      setLessonPlanErrorMessage(
        error?.message ||
        translateText('academics.lessonPlans.errorMessage') ||
        'An error occurred while saving the lesson plan'
      );
    } finally {
      setIsLessonPlanLoading(false);
    }
  };

  return (
    <div data-testid="add-edit-lesson-plan-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isCreateMode
            ? translateText('academics.lessonPlans.addLessonPlan') ||
            'Add Lesson Plan'
            : translateText('academics.lessonPlans.editLessonPlan') ||
            'Edit Lesson Plan'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isCreateMode
            ? translateText('common.save')
            : translateText('common.update')
        }
        isLoading={isLessonPlanLoading || contextIsLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          lessonPlanErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="lesson-plan-error-message"
            >
              {lessonPlanErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            data-testid="lesson-plan-form"
            sx={{ py: { xs: 2, sm: 4 } }}
          >
            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="title"
                id="lesson-plan-title"
                control={control}
                label={translateText('academics.lessonPlans.columns.title')}
                errorMessage={errors.title?.message}
                data-testid="lesson-plan-title-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledSelectField
                name="status"
                id="lesson-plan-status"
                control={control}
                label={translateText('academics.lessonPlans.columns.status')}
                itemList={getStatusOptions(translateText)}
                errorMessage={errors.status?.message}
                isGridItem={false}
                dataTestId="lesson-plan-status-field"
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledTextField
                name="description"
                id="lesson-plan-description"
                control={control}
                label={
                  translateText('academics.lessonPlans.description') ||
                  'Description'
                }
                errorMessage={errors.description?.message}
                multiline
                rows={3}
                data-testid="lesson-plan-description-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="academicYear"
                id="lesson-plan-academic-year"
                control={control}
                label={translateText(
                  'academics.lessonPlans.columns.academicYear'
                )}
                errorMessage={errors.academicYear?.message}
                data-testid="lesson-plan-academic-year-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="term"
                id="lesson-plan-term"
                control={control}
                label={translateText('academics.lessonPlans.columns.term')}
                errorMessage={errors.term?.message}
                data-testid="lesson-plan-term-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledSelectField
                name="board"
                id="lesson-plan-board"
                control={control}
                label={translateText('academics.lessonPlans.columns.board')}
                itemList={getBoardOptions(translateText)}
                errorMessage={errors.board?.message}
                isGridItem={false}
                dataTestId="lesson-plan-board-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledSelectField
                name="curriculumFramework"
                id="lesson-plan-curriculum-framework"
                control={control}
                label={translateText(
                  'academics.lessonPlans.columns.curriculumFramework'
                )}
                itemList={getCurriculumFrameworkOptions(translateText)}
                errorMessage={errors.curriculumFramework?.message}
                isGridItem={false}
                dataTestId="lesson-plan-curriculum-framework-field"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ControlledSelectField
                name="classId"
                id="lesson-plan-class"
                control={control}
                label={translateText('academics.lessonPlans.class') || 'Class'}
                itemList={classOptions}
                errorMessage={errors.classId?.message}
                isGridItem={false}
                dataTestId="lesson-plan-class-field"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ControlledSelectField
                name="courseId"
                id="lesson-plan-course"
                control={control}
                label={
                  translateText('academics.lessonPlans.course') || 'Course'
                }
                itemList={courseOptions}
                errorMessage={errors.courseId?.message}
                isGridItem={false}
                dataTestId="lesson-plan-course-field"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ControlledSelectField
                name="teacherId"
                id="lesson-plan-teacher"
                control={control}
                label={
                  translateText('academics.lessonPlans.teacher') || 'Teacher'
                }
                itemList={teacherOptions}
                errorMessage={errors.teacherId?.message}
                isGridItem={false}
                dataTestId="lesson-plan-teacher-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="subject"
                id="lesson-plan-subject"
                control={control}
                label={translateText('academics.lessonPlans.columns.subject')}
                errorMessage={errors.subject?.message}
                data-testid="lesson-plan-subject-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="gradeLevel"
                id="lesson-plan-grade-level"
                control={control}
                label={translateText(
                  'academics.lessonPlans.columns.gradeLevel'
                )}
                errorMessage={errors.gradeLevel?.message}
                data-testid="lesson-plan-grade-level-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="syllabusReference"
                id="lesson-plan-syllabus-reference"
                control={control}
                label={translateText(
                  'academics.lessonPlans.columns.syllabusReference'
                )}
                errorMessage={errors.syllabusReference?.message}
                data-testid="lesson-plan-syllabus-reference-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="textbookReference"
                id="lesson-plan-textbook-reference"
                control={control}
                label={translateText(
                  'academics.lessonPlans.columns.textbookReference'
                )}
                errorMessage={errors.textbookReference?.message}
                data-testid="lesson-plan-textbook-reference-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="ncertChapter"
                id="lesson-plan-ncert-chapter"
                control={control}
                label={
                  translateText('academics.lessonPlans.ncertChapter') ||
                  'NCERT Chapter'
                }
                errorMessage={errors.ncertChapter?.message}
                data-testid="lesson-plan-ncert-chapter-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="tags"
                id="lesson-plan-tags"
                control={control}
                label={translateText('academics.lessonPlans.columns.tags')}
                errorMessage={errors.tags?.message}
                data-testid="lesson-plan-tags-field"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledTextField
                name="totalDuration"
                id="lesson-plan-total-duration"
                control={control}
                label={
                  translateText('academics.lessonPlans.totalDuration') ||
                  'Total Duration (days)'
                }
                type="number"
                errorMessage={errors.totalDuration?.message}
                data-testid="lesson-plan-total-duration-field"
              />
            </Grid>

            {mode === 'edit' && (
              <Grid item xs={12} md={6}>
                <ControlledTextField
                  name="version"
                  id="lesson-plan-version"
                  control={control}
                  label={
                    translateText('academics.lessonPlans.version') || 'Version'
                  }
                  type="number"
                  errorMessage={errors.version?.message}
                  disabled
                  data-testid="lesson-plan-version-field"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1, sm: 2 } }} />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 0 },
                  mb: { xs: 1, sm: 2 },
                }}
              >
                <Typography variant="h6" data-testid="documents-section-title" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translateText('academics.lessonPlans.documents') ||
                    'Documents'}
                </Typography>
                <Button
                  startIcon={<Add />}
                  onClick={() => {
                    setValue('tempDocument', {
                      documentName: '',
                      title: '',
                      description: '',
                      verificationDate: '',
                      status: '',
                    });
                  }}
                  variant="outlined"
                  size="small"
                  sx={{
                    minHeight: { xs: '44px', sm: '32px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="add-document-button"
                >
                  {translateText('academics.lessonPlans.addDocument') ||
                    'Add Document'}
                </Button>
              </Box>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                item
                xs={12}
                key={field.id}
                data-testid={`lesson-plan-document-${index}`}
              >
                <Box
                  sx={{
                    border: '1px solid var(--border_grey)',
                    borderRadius: 1,
                    p: { xs: 1, sm: 2 },
                    mb: { xs: 1, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {translateText('documents.document') || 'Document'}{' '}
                      {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => remove(index)}
                      color="error"
                      size="small"
                      sx={{
                        minHeight: { xs: '44px', sm: '40px' },
                        minWidth: { xs: '44px', sm: '40px' },
                      }}
                      data-testid={`remove-document-${index}-button`}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                  <ControlledFileUploadField
                    control={control}
                    name={`documents.${index}.documentName`}
                    id={`lesson-plan-document-${index}`}
                    label={
                      translateText('documents.documentFile') || 'Document File'
                    }
                    sectionTitle={`${translateText('documents.document') || 'Document'} ${index + 1}`}
                    titleName={`documents.${index}.title`}
                    descriptionName={`documents.${index}.description`}
                    statusName={`documents.${index}.status`}
                    verificationName={`documents.${index}.verificationDate`}
                    setValue={setValue}
                    setError={methods.setError}
                    clearErrors={methods.clearErrors}
                  />
                </Box>
              </Grid>
            ))}

            {tempDocument && (
              <Grid item xs={12} data-testid="temp-document-field">
                <Box
                  sx={{
                    border: '1px solid var(--border_grey)',
                    borderRadius: 1,
                    p: { xs: 1, sm: 2 },
                    mb: { xs: 1, sm: 2 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {translateText('documents.newDocument') || 'New Document'}
                  </Typography>
                  <ControlledFileUploadField
                    control={control}
                    name="tempDocument.documentName"
                    id="lesson-plan-temp-document"
                    label={
                      translateText('documents.documentFile') || 'Document File'
                    }
                    sectionTitle={
                      translateText('documents.newDocument') || 'New Document'
                    }
                    titleName="tempDocument.title"
                    descriptionName="tempDocument.description"
                    statusName="tempDocument.status"
                    verificationName="tempDocument.verificationDate"
                    setValue={setValue}
                    setError={methods.setError}
                    clearErrors={methods.clearErrors}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditLessonPlanView;
